import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, Printer, ArrowRight, CheckCircle2 } from 'lucide-react';
import { brand } from '../mock/mockData';
import { useToast } from '../hooks/use-toast';
import api from '../lib/api';

const servicesList = ['General Enquiry', 'Commercial Property', 'Company Commercial', 'Dispute Resolution', 'Employment Law', 'Estate Agents', 'Private Client', 'Property Development', 'Residential Conveyancing', 'Social Housing', 'Other'];

export default function Contact() {
  const { toast } = useToast();
  const [form, setForm] = useState({ name: '', email: '', phone: '', client_type: 'New', service: 'General Enquiry', message: '', consent: false });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = 'Required';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = 'Valid email required';
    if (!form.message.trim()) e.message = 'Required';
    if (!form.consent) e.consent = 'You must agree to the privacy policy';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      await api.post('/contact', form);
      setSent(true);
      toast({ title: 'Enquiry sent', description: 'Thank you, we will be in touch shortly.' });
      setForm({ name: '', email: '', phone: '', client_type: 'New', service: 'General Enquiry', message: '', consent: false });
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast({ title: 'Unable to send', description: typeof detail === 'string' ? detail : 'Please try again in a moment.' });
    } finally { setSubmitting(false); }
  };

  return (
    <div className="pt-24">
      <section className="bg-forest-950 py-24">
        <div className="max-w-5xl mx-auto px-6 md:px-10">
          <p className="text-sage-400 tracking-[0.3em] text-xs uppercase mb-5 reveal">Get in touch</p>
          <h1 className="font-serif text-cream-50 text-5xl md:text-7xl leading-tight reveal">Contact Us</h1>
          <p className="mt-6 text-cream-100/75 text-lg max-w-2xl reveal">If you have a general enquiry please use the form below and we will endeavour to get back to you as soon as possible. For direct contact with a specific solicitor, please visit our Team page.</p>
        </div>
      </section>

      <section className="bg-forest-900 py-20">
        <div className="max-w-6xl mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-5 gap-12">
          <div className="lg:col-span-2 space-y-8">
            <div>
              <h3 className="font-serif text-cream-50 text-2xl mb-4">Our Office</h3>
              <div className="flex gap-3 text-cream-100/85"><MapPin className="h-5 w-5 text-sage-400 mt-1 flex-shrink-0" /><div><p>{brand.address.line1}</p><p>{brand.address.line2}</p><p>{brand.address.city}, {brand.address.postcode}</p></div></div>
            </div>
            <div className="space-y-3">
              <div className="flex gap-3"><Phone className="h-5 w-5 text-sage-400 mt-1" /><a href={`tel:${brand.phone.replace(/\s/g,'')}`} className="text-cream-100/85 hover:text-sage-400">Tel. {brand.phone}</a></div>
              <div className="flex gap-3"><Printer className="h-5 w-5 text-sage-400 mt-1" /><span className="text-cream-100/85">Fax. {brand.fax}</span></div>
              <div className="flex gap-3"><Mail className="h-5 w-5 text-sage-400 mt-1" /><a href={`mailto:${brand.publicEmail}`} className="text-cream-100/85 hover:text-sage-400 break-all">{brand.publicEmail}</a></div>
            </div>
            <div className="pt-4 border-t border-forest-800 text-sm text-cream-100/65 space-y-2">
              <p>If you would like to read how we manage your data, please read our <a href="https://hedgeslaw.co.uk/privacy/" target="_blank" rel="noopener noreferrer" className="text-sage-400 hover:underline">privacy policy</a>.</p>
              <p>Have a complaint or concern about our service? Please read our <Link to="/complaints" data-testid="complaints-link" className="text-sage-400 hover:underline">Client Complaints Policy</Link> for full details of how we will handle it.</p>
            </div>
          </div>

          {sent ? (
            <div className="lg:col-span-3 bg-forest-950 border border-sage-500/40 p-10 flex flex-col items-center text-center">
              <CheckCircle2 className="h-14 w-14 text-sage-400" />
              <h2 className="mt-5 font-serif text-cream-50 text-3xl">Thank you</h2>
              <p className="mt-3 text-cream-100/75 max-w-md">Your enquiry has been received. A member of the Perrin Myddelton team will be in touch shortly.</p>
              <button onClick={() => setSent(false)} className="mt-6 text-sage-400 text-sm hover:underline">Send another enquiry</button>
            </div>
          ) : (
            <form onSubmit={onSubmit} className="lg:col-span-3 bg-forest-950 border border-forest-800 p-8 space-y-5" noValidate>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className="block text-xs uppercase tracking-[0.2em] text-cream-100/60 mb-2">Name *</label>
                  <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full bg-forest-900 border border-forest-700 focus:border-sage-500 outline-none px-4 py-3 text-cream-50 text-sm" maxLength={120} required />
                  {errors.name && <p className="text-red-400 text-xs mt-1">{errors.name}</p>}
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-[0.2em] text-cream-100/60 mb-2">Email *</label>
                  <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full bg-forest-900 border border-forest-700 focus:border-sage-500 outline-none px-4 py-3 text-cream-50 text-sm" maxLength={160} required />
                  {errors.email && <p className="text-red-400 text-xs mt-1">{errors.email}</p>}
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-[0.2em] text-cream-100/60 mb-2">Phone</label>
                  <input type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="w-full bg-forest-900 border border-forest-700 focus:border-sage-500 outline-none px-4 py-3 text-cream-50 text-sm" maxLength={30} />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-[0.2em] text-cream-100/60 mb-2">Client type</label>
                  <select value={form.client_type} onChange={(e) => setForm({ ...form, client_type: e.target.value })} className="w-full bg-forest-900 border border-forest-700 focus:border-sage-500 outline-none px-4 py-3 text-cream-50 text-sm">
                    <option>New</option><option>Returning</option><option>N/A</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-xs uppercase tracking-[0.2em] text-cream-100/60 mb-2">Service required</label>
                <select value={form.service} onChange={(e) => setForm({ ...form, service: e.target.value })} className="w-full bg-forest-900 border border-forest-700 focus:border-sage-500 outline-none px-4 py-3 text-cream-50 text-sm">
                  {servicesList.map((s) => <option key={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs uppercase tracking-[0.2em] text-cream-100/60 mb-2">Message *</label>
                <textarea rows={6} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="w-full bg-forest-900 border border-forest-700 focus:border-sage-500 outline-none px-4 py-3 text-cream-50 text-sm resize-none" maxLength={4000} required />
                {errors.message && <p className="text-red-400 text-xs mt-1">{errors.message}</p>}
              </div>
              <label className="flex items-start gap-3 text-sm text-cream-100/70 cursor-pointer">
                <input type="checkbox" checked={form.consent} onChange={(e) => setForm({ ...form, consent: e.target.checked })} className="mt-1 accent-sage-500" />
                <span>I confirm that I agree with the <a href="https://hedgeslaw.co.uk/privacy/" target="_blank" rel="noopener noreferrer" className="text-sage-400 hover:underline">privacy policy</a>.</span>
              </label>
              {errors.consent && <p className="text-red-400 text-xs">{errors.consent}</p>}
              <button type="submit" disabled={submitting} className="group inline-flex items-center gap-3 bg-sage-500 text-forest-900 px-8 py-4 text-sm font-medium tracking-wide hover:bg-sage-600 transition-colors disabled:opacity-60">
                {submitting ? 'Sending…' : 'Submit Enquiry'} <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </button>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}
